import { memo, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { formatRelative } from "@/lib/issues";
import type { Issue, ProjectData } from "@/lib/store";
import type { ViewSettings } from "@/lib/views";
import { AssigneePicker, DueDateButton, LabelPicker, PriorityPicker, StatePicker } from "./IssueProps";

export const IssueRow = memo(function IssueRow({
  d,
  issue,
  view,
  focused,
  selected,
  onFocus,
  onToggleSelect,
  pickers,
  showProject,
  draggable,
  onDragStart,
  treeControls,
  depth = 0,
}: {
  d: ProjectData;
  issue: Issue;
  view: ViewSettings;
  focused?: boolean;
  selected?: boolean;
  onFocus?: () => void;
  onToggleSelect?: () => void;
  /** Which picker a keyboard shortcut asked to open for this row. */
  pickers?: { open: "state" | "priority" | "assignee" | "label" | null; onClose: () => void };
  showProject?: boolean;
  treeControls?: ReactNode;
  depth?: number;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
}) {
  const { t, i18n } = useTranslation();
  const id = `${d.project.key}-${issue.number}`;
  const ctl = (k: NonNullable<typeof pickers>["open"]) =>
    pickers?.open === k ? { open: true, onOpenChange: (open: boolean) => !open && pickers.onClose() } : {};

  return (
    <div
      data-issue-id={issue.id}
      draggable={draggable}
      onDragStart={onDragStart}
      onMouseEnter={onFocus}
      onFocusCapture={onFocus}
      className={`@container/issue group border-b border-border/60 text-[13px] transition-colors ${selected ? `bg-accent/10 shadow-[inset_2px_0_0_var(--accent)] ${focused ? "ring-1 ring-accent/30 ring-inset" : ""}` : focused ? "bg-hover" : "hover:bg-hover/60"}`}
    >
      <div className="flex min-h-10 flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2 @2xl/issue:flex-nowrap @2xl/issue:py-0" style={{ paddingLeft: 16 + depth * 20 }}>
        {treeControls}
        {onToggleSelect && (
          <input
            type="checkbox"
            className={`h-3.5 w-3.5 shrink-0 cursor-pointer rounded accent-accent transition-opacity ${selected ? "opacity-100" : "opacity-60 @2xl/issue:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"}`}
            checked={!!selected}
            onChange={onToggleSelect}
            onClick={(e) => e.stopPropagation()}
            aria-label={t("issue.selectIssue", { identifier: id })}
          />
        )}
        <div className="shrink-0"><PriorityPicker issue={issue} compact {...ctl("priority")} /></div>
        {view.props.id && (
          <span className="w-[72px] shrink-0 truncate font-mono text-[11.5px] tabular-nums text-muted" title={showProject ? `${d.project.name} · ${id}` : id}>{id}</span>
        )}
        <div className="shrink-0"><StatePicker d={d} issue={issue} compact {...ctl("state")} /></div>
        <Link
          draggable={false}
          to="/$ws/issue/$identifier"
          params={{ ws: d.slug, identifier: id }}
          className="min-w-0 flex-1 truncate py-1 leading-5 text-fg hover:underline"
          title={issue.title}
          onClick={(e) => e.stopPropagation()}
        >
          {issue.parent_id && depth === 0 && <span className="mr-1 text-faint">↳</span>}
          {issue.title}
        </Link>
        <div className="ml-auto flex min-w-0 basis-full flex-wrap items-center justify-end gap-x-2 gap-y-1 text-muted @2xl/issue:shrink-0 @2xl/issue:basis-auto @2xl/issue:flex-nowrap">
          {view.props.labels && issue.label_ids.length > 0 && <div className="max-w-24 overflow-hidden"><LabelPicker d={d} issue={issue} compact {...ctl("label")} /></div>}
          {view.props.estimate && issue.estimate != null && <span className="min-w-5 rounded border border-border px-1 text-center text-[11px] tabular-nums" title={t("issue.estimate")}>{issue.estimate}</span>}
          {view.props.due && issue.due_date && <div className="shrink-0 whitespace-nowrap"><DueDateButton issue={issue} compact /></div>}
          {view.props.created && <span className="min-w-[52px] whitespace-nowrap text-right text-[11.5px] tabular-nums text-faint" title={t("view.created")}>{formatRelative(issue.created_at, i18n.language)}</span>}
          {view.props.assignee && <AssigneePicker d={d} issue={issue} compact {...ctl("assignee")} />}
        </div>
      </div>
    </div>
  );
});
